import { useMutation } from '@tanstack/react-query'
import { Suspense, useEffect, useRef, useState } from 'react'

export function cancelDialog(ev: {
	preventDefault(): void
	currentTarget: HTMLElement
}) {
	ev.preventDefault()
	const dialog = ev.currentTarget.closest('dialog')
	if (dialog?.dispatchEvent(new Event('cancel', {cancelable: true}))) {
		dialog.close()
	}
}

export function CancelButton({children}: {children: any}) {
	// formmethod="dialog" is not good enough, because it fires the close event, not the cancel event.

	return <button onClick={cancelDialog}>
		{children}
	</button>
}

export function Dialog({mutation, children}: {
	mutation: InteractiveMutation,
	children: any,
}) {
	const mydialog = useRef<HTMLDialogElement>(null)

	useEffect(() => {
		const dialog = mydialog.current!
		if (mutation.cancel) {
			dialog.showModal()
			dialog.classList.add('shown')
		}
		else if (dialog.classList.contains('shown')) {
			dialog.classList.remove('shown')
			setTimeout(() => {
				dialog.close()
			}, 200)
		}
	}, [mutation.cancel])

	return <dialog className="basic" ref={mydialog} onCancel={ev => {
		if (ev.defaultPrevented) return
		ev.preventDefault()
		mutation.cancel!()
	}}>
		<Suspense>
			{children}
		</Suspense>
	</dialog>
}

export interface InteractiveMutation {
	cancel: (() => void) | null
}

export interface IntentMutation<TIntent> extends InteractiveMutation {
	mutateAsync: () => Promise<void>
	isPending: boolean
	intent: Promise<TIntent> | null
}

interface CancellableIntent<TIntent> {
	intent: Promise<TIntent>
	cancel: () => void
}

export function useDeclareIntent<TIntent>(start: () => Promise<TIntent>): IntentMutation<TIntent> {
	const [cancellableIntent, setCancellableIntent] = useState<CancellableIntent<TIntent> | null>(null)
	const mut = useMutation({
		mutationFn: async () => {
			await new Promise<void>((resolve, reject) => {
				const intent = start()
				setCancellableIntent({
					intent,
					cancel: resolve
				})
				intent.then(intent => {if (!intent) resolve()}, reason => {reject(reason)})
			})
			setCancellableIntent(null)
		}
	})
	return {
		mutateAsync: mut.mutateAsync,
		isPending: cancellableIntent === null ? false : true,
		intent: cancellableIntent === null ? null : cancellableIntent.intent,
		cancel: cancellableIntent === null ? null : cancellableIntent.cancel,
	}
}
